
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

interface FeedbackSectionProps {
  articleTitle: string;
  articleId: string;
}

const FeedbackSection = ({ articleTitle, articleId }: FeedbackSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [isSending, setIsSending] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);

    try {
      // Use authenticated user's email if available, otherwise use form input
      const userEmail = user?.email || email;
      
      if (!userEmail && !email) {
        throw new Error("Email is required");
      }

      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          subject: `Article Feedback: ${articleTitle}`,
          html: `
            <h2>New Article Feedback</h2>
            <p><strong>From:</strong> ${name} (${userEmail})</p>
            <p><strong>Article:</strong> ${articleTitle} (ID: ${articleId})</p>
            <p><strong>Message:</strong></p>
            <p>${message.replace(/\n/g, "<br>")}</p>
          `,
          text: `New Article Feedback\n\nFrom: ${name} (${userEmail})\nArticle: ${articleTitle} (ID: ${articleId})\n\nMessage:\n${message}`,
          name,
          replyTo: userEmail
        },
      });

      if (error) {
        console.error("Supabase function error:", error);
        throw new Error("Failed to send feedback");
      }

      toast({
        title: "Feedback sent",
        description: "Thank you for your feedback!",
      });
      setEmail("");
      setMessage("");
      setName("");
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to send feedback:", error);
      toast({
        title: "Error sending feedback",
        description: error instanceof Error ? error.message : "Please try again later.",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="mt-12 border-t pt-8">
      {!isOpen ? (
        <div className="text-center">
          <Button 
            onClick={() => setIsOpen(true)} 
            variant="outline" 
            className="flex mx-auto items-center gap-2 transition-all hover:bg-muted"
          >
            <MessageSquare className="h-4 w-4" />
            Share your feedback
          </Button>
        </div>
      ) : (
        <div className="bg-muted/30 p-6 rounded-lg border">
          <h3 className="text-lg font-medium mb-4">Seeking feedback - did you find this article useful?</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">
                  Name
                </label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>
              {!user?.email && (
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1">
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                  />
                </div>
              )}
              {user?.email && (
                <div>
                  <label htmlFor="email-display" className="block text-sm font-medium mb-1">
                    Email
                  </label>
                  <Input
                    id="email-display"
                    type="email"
                    value={user.email}
                    disabled
                    className="bg-muted text-muted-foreground"
                  />
                </div>
              )}
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium mb-1">
                Message
              </label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What did you think about this article? Any suggestions or questions?"
                rows={4}
                required
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsOpen(false)}
                disabled={isSending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSending}>
                {isSending ? "Sending..." : "Send Feedback"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default FeedbackSection;
